import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";
import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { IconPicker } from "@/components/ui/icon-picker";
import { Input } from "@/components/ui/input";
import { useCMSTabContext } from "@/components/web-experience/cms-tab-context";
import { SKUPageContentGenericTabIcon, SKUPageContentTab } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";

import { icons } from "./data";

type Props = {
  options: SKUPageContentTab["options"];
  defaultTitle?: string;
  defaultIcon?: string;
};

const cmsTabDetailsFormSchema = z.object({
  title: z.string().max(15).optional(),
  icon: z.string().optional(),
});

export const TabDetails: FC<Props> = ({
  options,
  defaultTitle,
  defaultIcon,
}) => {
  const { tabIndex } = useCMSTabContext();

  const onCustomize = useCMSStore.use.onCustomizeTab();

  const form = useForm({
    resolver: zodResolver(cmsTabDetailsFormSchema),
    defaultValues: {
      title: get(options, "tab_title", defaultTitle),
      icon: get(options, "tab_icon", defaultIcon),
    },
  });

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        onCustomize(
          tabIndex,
          values.title,
          values.icon as SKUPageContentGenericTabIcon,
        );
      },
    });

    return unsubscribe;
  }, [form, onCustomize, tabIndex]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium leading-none">Tab</CardTitle>
        <CardDescription>
          Customize the tab icon and name below. Max of 15 characters
        </CardDescription>
      </CardHeader>

      <Form {...form}>
        <CardContent className="p-6 pt-0 flex flex-row gap-2 space-y-4">
          <FormField
            control={form.control}
            name="icon"
            render={({ field }) => (
              <FormItem className="flex">
                <FormControl>
                  <IconPicker
                    icons={icons}
                    value={field.value as SKUPageContentGenericTabIcon}
                    onValueChange={field.onChange}
                    categorized={false}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="flex flex-grow">
                <FormControl>
                  <Input placeholder={defaultTitle} maxLength={15} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        </CardContent>
      </Form>
    </Card>
  );
};
